import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { createHeroSlide, updateHeroSlide, getAllHeroSlides } from '../api/adminApi';
import { getAssetUrl } from '../../api/client';
import ImageUploadField from '../components/ImageUploadField';
import '../adminTheme.css';
import './adminForms.css';

const emptyForm = { title: '', href: '/', order: 0, isActive: true };

export default function HeroFormPage() {
  const { id } = useParams();
  const isEditing = Boolean(id);
  const { token } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState(emptyForm);
  const [imageFile, setImageFile] = useState(null);
  const [existingImage, setExistingImage] = useState('');
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isEditing) return;
    // There's no GET /api/hero/:id on the backend - find this slide from the
    // admin "all slides" list instead.
    getAllHeroSlides(token)
      .then((slides) => {
        const slide = slides.find((s) => s._id === id);
        if (!slide) throw new Error('Hero slide not found');
        setForm({ title: slide.title, href: slide.href, order: slide.order, isActive: slide.isActive });
        setExistingImage(getAssetUrl(slide.imgUrl));
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [id, isEditing, token]);

  function updateField(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (!isEditing && !imageFile) {
      setError('Please choose a hero image (1920x1080 recommended).');
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('title', form.title);
      formData.append('href', form.href);
      formData.append('order', form.order);
      formData.append('isActive', String(form.isActive));
      if (imageFile) formData.append('image', imageFile);

      if (isEditing) {
        await updateHeroSlide(token, id, formData);
      } else {
        await createHeroSlide(token, formData);
      }
      navigate('/admin/hero');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <p style={{ color: '#999' }}>Loading...</p>;

  return (
    <div className="cs-admin_form_page">
      <div className="cs-admin_page_header">
        <h1>{isEditing ? 'Edit Hero Slide' : 'Add Hero Slide'}</h1>
        <Link to="/admin/hero" className="cs-admin_btn cs-admin_btn-ghost">
          ← Back to list
        </Link>
      </div>

      {error && <div className="cs-admin_error">{error}</div>}

      <form onSubmit={handleSubmit} className="cs-admin_form">
        <label>Title *</label>
        <textarea
          value={form.title}
          onChange={(e) => updateField('title', e.target.value)}
          placeholder="Think reality <br/>do positive"
          rows={2}
          required
        />
        <small className="cs-admin_hint">Use &lt;br/&gt; for a line break, same as the original static data.</small>

        <label>Link (href)</label>
        <input value={form.href} onChange={(e) => updateField('href', e.target.value)} placeholder="/" />

        <div className="cs-admin_form_row">
          <div>
            <label>Slider order</label>
            <input type="number" value={form.order} onChange={(e) => updateField('order', e.target.value)} />
          </div>
          <div className="cs-admin_checkbox_field">
            <label>
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => updateField('isActive', e.target.checked)}
              />
              Active (visible on homepage)
            </label>
          </div>
        </div>

        <ImageUploadField
          label="Hero image (1920x1080 recommended)"
          existingImageUrl={existingImage}
          onChange={setImageFile}
          required={!isEditing}
        />

        <button type="submit" className="cs-admin_btn cs-admin_btn-primary" disabled={saving}>
          {saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Slide'}
        </button>
      </form>
    </div>
  );
}
